import api from './api';

export const observationService = {
  // =============================================================================
  // OBSERVATION MANAGEMENT
  // =============================================================================

  // Get all observations with pagination and filtering
  async getObservations(params = {}) {
    try {
      const response = await api.get('/observations', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch observations');
    }
  },

  // Get observation by ID
  async getObservation(id) {
    try {
      const response = await api.get(`/observations/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch observation');
    }
  },

  // Create new observation
  async createObservation(observationData) {
    try {
      const response = await api.post('/observations', observationData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create observation');
    }
  },

  // Update observation
  async updateObservation(id, observationData) {
    try {
      const response = await api.put(`/observations/${id}`, observationData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update observation');
    }
  },

  // Delete observation
  async deleteObservation(id) {
    try {
      const response = await api.delete(`/observations/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete observation');
    }
  },

  // Bulk create observations
  async bulkCreateObservations(observationsData) {
    try {
      const response = await api.post('/observations/bulk', observationsData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to bulk create observations');
    }
  },

  // =============================================================================
  // OBSERVATION FILTERING AND SEARCH
  // =============================================================================

  // Get observations by trial
  async getObservationsByTrial(trialId, params = {}) {
    try {
      const response = await api.get(`/trials/${trialId}/observations`, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch observations by trial');
    }
  },

  // Get observations by plot
  async getObservationsByPlot(plotId, params = {}) {
    try {
      const response = await api.get(`/observations?plot_id=${plotId}`, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch observations by plot');
    }
  },

  // Get observations by date range
  async getObservationsByDateRange(startDate, endDate, params = {}) {
    try {
      const response = await api.get('/observations', {
        params: {
          ...params,
          startDate,
          endDate,
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch observations by date range');
    }
  },

  // Get observations by type
  async getObservationsByType(observationType, params = {}) {
    try {
      const response = await api.get(`/observations?observationType=${observationType}`, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch observations by type');
    }
  },

  // Get observations by officer
  async getObservationsByOfficer(officerId, params = {}) {
    try {
      const response = await api.get(`/observations?officer_id=${officerId}`, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch observations by officer');
    }
  },

  // Advanced observation search
  async searchObservations(searchCriteria) {
    try {
      const response = await api.post('/search/observations', searchCriteria);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to search observations');
    }
  },

  // =============================================================================
  // OBSERVATION STATISTICS
  // =============================================================================

  // Get observation statistics
  async getObservationStats() {
    try {
      const response = await api.get('/observations/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch observation statistics');
    }
  },

  // Get observations by month
  async getObservationsByMonth(year, trialId = null) {
    try {
      const params = { year };
      if (trialId) params.trialId = trialId;
      
      const response = await api.get('/observations/stats/by-month', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch observations by month');
    }
  },

  // Get observation types summary
  async getObservationTypesSummary(trialId = null) {
    try {
      const params = {};
      if (trialId) params.trialId = trialId;
      
      const response = await api.get('/observations/stats/by-type', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch observation types summary');
    }
  },

  // =============================================================================
  // OBSERVATION EXPORT
  // =============================================================================

  // Export observations to Excel
  async exportObservations(params = {}) {
    try {
      const response = await api.get('/observations/export/excel', {
        params,
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export observations');
    }
  },

  // Export trial observations to Excel
  async exportTrialObservations(trialId, params = {}) {
    try {
      const response = await api.get(`/trials/${trialId}/observations/export/excel`, {
        params,
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export trial observations');
    }
  },

  // =============================================================================
  // OBSERVATION TEMPLATES
  // =============================================================================

  // Get observation templates
  async getObservationTemplates() {
    try {
      const response = await api.get('/observations/templates');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch observation templates');
    }
  },

  // Create observation template
  async createObservationTemplate(templateData) {
    try {
      const response = await api.post('/observations/templates', templateData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create observation template');
    }
  },

  // Update observation template
  async updateObservationTemplate(id, templateData) {
    try {
      const response = await api.put(`/observations/templates/${id}`, templateData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update observation template');
    }
  },

  // Delete observation template
  async deleteObservationTemplate(id) {
    try {
      const response = await api.delete(`/observations/templates/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete observation template');
    }
  },
};
